import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';
import { cn } from '@/shared/utils/cn';

export type SearchableSelectOption = {
  value: string;
  label: string;
  description?: string;
};

type SearchableSelectProps = {
  label?: string;
  required?: boolean;
  error?: string;
  placeholder?: string;
  /** Text shown in the input when a value is selected and the field is not being edited. */
  selectedLabel?: string;
  options: SearchableSelectOption[];
  loading?: boolean;
  emptyText?: string;
  disabled?: boolean;
  /** Whether more pages can be loaded (drives infinite scroll). */
  hasMore?: boolean;
  /** True while the next page is being fetched (shows a loading row). */
  loadingMore?: boolean;
  /** Called when the list is scrolled near the bottom; should fetch the next page. */
  onLoadMore?: () => void;
  /** Fires when the dropdown opens — use to trigger the initial (q="") fetch. */
  onOpen?: () => void;
  /** Fires when the dropdown closes — use to disable async fetching. */
  onClose?: () => void;
  /** Fires on every keystroke (already-debounced consumption is the caller's job). */
  onSearch: (term: string) => void;
  onSelect: (option: SearchableSelectOption) => void;
};

/** Trigger onLoadMore when the scroll position is within this many px of the bottom. */
const SCROLL_THRESHOLD_PX = 48;

export const SearchableSelect = ({
  label,
  required,
  error,
  placeholder,
  selectedLabel,
  options,
  loading,
  emptyText,
  disabled,
  hasMore,
  loadingMore,
  onLoadMore,
  onOpen,
  onClose,
  onSearch,
  onSelect,
}: SearchableSelectProps) => {
  const autoId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (wasOpenRef.current && !open) onClose?.();
    wasOpenRef.current = open;
  }, [open, onClose]);

  const openDropdown = () => {
    setOpen((wasOpen) => {
      if (!wasOpen) onOpen?.();
      return true;
    });
  };

  const onListScroll = (e: React.UIEvent<HTMLUListElement>) => {
    if (!hasMore || loadingMore || !onLoadMore) return;
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_THRESHOLD_PX) {
      onLoadMore();
    }
  };

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    setActive(0);
  }, [options]);

  const pick = (opt: SearchableSelectOption) => {
    onSelect(opt);
    setQuery('');
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      openDropdown();
      setActive((i) => Math.min(i + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      const opt = options[active];
      if (open && opt) {
        e.preventDefault();
        pick(opt);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const displayValue = open ? query : (selectedLabel ?? query);

  return (
    <div className="flex flex-col gap-1.5" ref={containerRef}>
      {label && (
        <label htmlFor={autoId} className="text-sm font-medium text-ink-soft">
          {required && (
            <span aria-hidden="true" className="mr-0.5 text-red-600">
              *
            </span>
          )}
          {label}
        </label>
      )}
      <div className="relative">
        <div
          className={cn(
            'flex items-center gap-2 rounded-lg border bg-white px-3 shadow-sm transition-colors',
            error
              ? 'border-red-400 focus-within:border-red-500 focus-within:ring-2 focus-within:ring-red-200'
              : 'border-slate-300 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-200',
            disabled && 'opacity-60',
          )}
        >
          <input
            id={autoId}
            role="combobox"
            aria-expanded={open}
            aria-controls={`${autoId}-listbox`}
            aria-invalid={Boolean(error)}
            autoComplete="off"
            disabled={disabled}
            value={displayValue}
            placeholder={placeholder}
            onFocus={openDropdown}
            onChange={(e) => {
              const v = e.target.value;
              setQuery(v);
              openDropdown();
              onSearch(v);
            }}
            onKeyDown={onKeyDown}
            className={cn(
              'min-w-0 flex-1 bg-transparent py-2.5 text-sm text-ink outline-none placeholder:text-ink-faint',
              'disabled:cursor-not-allowed',
            )}
          />
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin text-ink-muted" />
          ) : (
            <ChevronDown className="h-4 w-4 text-ink-muted" />
          )}
        </div>
        {open && (
          <ul
            id={`${autoId}-listbox`}
            role="listbox"
            onScroll={onListScroll}
            className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-lg border border-slate-200 bg-white py-1 text-sm shadow-lg"
          >
            {options.length === 0 ? (
              <li className="px-3 py-2 text-ink-muted">
                {(loading ? null : emptyText) as ReactNode}
                {loading ? '…' : null}
              </li>
            ) : (
              <>
                {options.map((opt, i) => (
                  <li
                    key={opt.value}
                    role="option"
                    aria-selected={i === active}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      pick(opt);
                    }}
                    onMouseEnter={() => setActive(i)}
                    className={cn(
                      'cursor-pointer px-3 py-2',
                      i === active ? 'bg-brand-50 text-ink' : 'text-ink-soft',
                    )}
                  >
                    <span className="block font-medium">{opt.label}</span>
                    {opt.description && (
                      <span className="block text-xs text-ink-muted">{opt.description}</span>
                    )}
                  </li>
                ))}
                {loadingMore && (
                  <li className="flex items-center justify-center gap-2 px-3 py-2 text-xs text-ink-muted">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  </li>
                )}
              </>
            )}
          </ul>
        )}
      </div>
      {error && <p className="text-xs font-medium text-red-600">{error}</p>}
    </div>
  );
};
